import classNames from 'classnames';
import type { Parchment } from 'quill/core';
import React, { useState } from 'react';
import { useAppDispatch } from '../../../app/redux/useAppSelector';
import Badge from '../../../components/Badge';
import InputTabs from '../../../components/InputTabs';
import SVGIcon from '../../../components/SVGIcon';
import alertIcon from '../../../icons/non-prefilled/16x16/alert.svg';
import boltIcon from '../../../icons/non-prefilled/16x16/bolt.svg';
import PricingModal from '../../../organization/PricingModal';
import { UIActions } from '../../../ui/uiSlice';
import { sanitizeActionLink } from '../../../utils/url/sanitizeUrl';
import type ButtonBlot from '../../formats/ButtonBlot';
import type { ButtonType } from '../../formats/ButtonBlot';
import * as styles from './ButtonLinkEditor.module.scss';

const unsavedNavigationDestination = new WeakMap<Parchment.Blot, string>();
const unsavedActionDestination = new WeakMap<Parchment.Blot, string>();

interface ButtonLinkEditorProps {
  blot: ButtonBlot;
  initialDestination: string;
  editingTarget: ButtonType;
  onEditingTargetChange: (target: ButtonType | null) => void;
  onSave: (value: string) => void;
  actionsEnabled: boolean;
}

const ButtonLinkEditor = ({
  blot,
  initialDestination,
  editingTarget,
  onEditingTargetChange,
  onSave,
  actionsEnabled,
}: ButtonLinkEditorProps) => {
  const dispatch = useAppDispatch();
  const currentType = blot.getState().type;

  const [navigationDestination, setNavigationDestination] = useState(
    unsavedActionDestination.get(blot) || currentType === 'navigation'
      ? initialDestination
      : '',
  );
  const [actionDestination, setActionDestination] = useState(
    unsavedActionDestination.get(blot) || currentType === 'action'
      ? initialDestination
      : '',
  );

  const sanitizedActionLink = sanitizeActionLink(actionDestination);

  const handleSave = () => {
    if (editingTarget === 'navigation') {
      onSave(navigationDestination);
      unsavedNavigationDestination.delete(blot);
    } else if (sanitizedActionLink) {
      onSave(sanitizedActionLink);
      unsavedActionDestination.delete(blot);
    }

    onEditingTargetChange(null);
  };

  return (
    <InputTabs.Root>
      <InputTabs.List>
        <InputTabs.TabButton
          active={editingTarget === 'navigation'}
          onClick={() => onEditingTargetChange('navigation')}
          aria-label="Edit navigation link"
        >
          Navigate
        </InputTabs.TabButton>
        <InputTabs.TabButton
          active={editingTarget === 'action'}
          onClick={(e) => {
            if (actionsEnabled) {
              onEditingTargetChange('action');
            } else {
              e.preventDefault();

              dispatch(
                UIActions.openModal(PricingModal, {
                  highlight: 'api',
                }),
              );
            }
          }}
          aria-label="Edit action"
        >
          <div
            className={classNames(styles.actionTabContainer, {
              [styles.enabled]: actionsEnabled,
            })}
          >
            {!actionsEnabled && (
              <SVGIcon className={styles.boltIcon}>{boltIcon}</SVGIcon>
            )}
            <div>Action</div>
            {actionsEnabled && <Badge>Beta</Badge>}
          </div>
        </InputTabs.TabButton>
      </InputTabs.List>
      {currentType === 'action' &&
      editingTarget === 'navigation' &&
      actionDestination !== '' ? (
        <div className={styles.navigateWarning}>
          <div>Remove Action to enable Navigate</div>
          <SVGIcon className={styles.errorIcon}>{alertIcon}</SVGIcon>
        </div>
      ) : (
        <InputTabs.Input
          onSave={handleSave}
          onEscape={() => onEditingTargetChange(null)}
          saveDisabled={
            editingTarget === 'action' ? !sanitizedActionLink : undefined
          }
          placeholder="Paste a link"
          value={
            editingTarget === 'navigation'
              ? navigationDestination
              : actionDestination
          }
          onChange={(value) => {
            if (editingTarget === 'navigation') {
              unsavedNavigationDestination.set(blot, value);
              setNavigationDestination(value);
            } else {
              unsavedActionDestination.set(blot, value);
              setActionDestination(value);
            }
          }}
        />
      )}
    </InputTabs.Root>
  );
};

export default ButtonLinkEditor;
